import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { doctorService } from '@/services/doctor_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { Doctor } from '@magnetar/data-access'
import { Label } from '@/components/ui/label'
import { useState, useEffect } from 'react'
import { Plus, X, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Uploader } from '@/components/Uploader'

export const formSchema = z.object({
  name: z.string().min(1, { message: '請輸入醫生姓名' }),
  specialty: z.array(z.string()).default([]),
  experience: z.array(z.string()).default([]),
  education: z
    .array(
      z.object({
        school: z.string(),
        year: z.string().optional(),
      }),
    )
    .default([]),
  description: z.string(),
  profile_image: z.string().optional(),
})

interface DoctorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: number
  onSuccess: () => void
  doctor?: Doctor
}

export function DoctorDialog({
  open,
  onOpenChange,
  clinicId,
  onSuccess,
  doctor,
}: DoctorDialogProps) {
  const { token } = useAuth()
  const isEditMode = !!doctor
  const [specialtyDialogOpen, setSpecialtyDialogOpen] = useState(false)
  const [newSpecialty, setNewSpecialty] = useState('')
  const [experienceDialogOpen, setExperienceDialogOpen] = useState(false)
  const [newExperience, setNewExperience] = useState('')
  const [educationDialogOpen, setEducationDialogOpen] = useState(false)
  const [newSchool, setNewSchool] = useState('')
  const [newYear, setNewYear] = useState('')
  const [currentProfileImageUuid, setCurrentProfileImageUuid] = useState<string | null | undefined>(
    null,
  )

  const form = useForm<z.infer<typeof formSchema>>({
    // resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      specialty: [],
      experience: [],
      education: [],
      description: '',
      profile_image: '',
    },
  })

  // 當編輯模式且doctor資料變更時，更新表單預設值
  useEffect(() => {
    if (isEditMode && doctor) {
      let educationData: { school: string; year?: string }[] = []
      try {
        if (doctor.education) {
          // 如果是字串，嘗試解析JSON
          if (typeof doctor.education === 'string') {
            educationData = JSON.parse(doctor.education)
          } else {
            // 否則直接使用
            educationData = doctor.education as { school: string; year?: string }[]
          }
        }
      } catch {
        // 解析失敗時設為空陣列
        educationData = []
      }

      form.reset({
        name: doctor.name,
        specialty: doctor.specialty || [],
        experience: doctor.experience || [],
        education: educationData,
        description: doctor.description || '',
        profile_image: doctor.profile_image || '',
      })
      setCurrentProfileImageUuid(doctor.profile_image)
    }
  }, [isEditMode, doctor, form])

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!token) return

    // 轉換資料格式
    const specialtyArray = values.specialty
    const experienceArray = values.experience
    const educationData = values.education

    if (isEditMode) {
      // 更新醫生資料
      const result = await doctorService.updateDoctor(
        doctor.doctor_id,
        {
          name: values.name,
          specialty: specialtyArray,
          experience: experienceArray,
          education: educationData,
          description: values.description,
          profile_image: values.profile_image || undefined,
        },
        token,
      )

      if (result.success) {
        toast.success('更新醫生資料成功')
        form.reset()
        setCurrentProfileImageUuid(null)
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error(result.error?.message || '更新醫生資料失敗')
      }
    } else {
      // 創建新醫生
      const result = await doctorService.createDoctor(
        {
          clinic_id: clinicId,
          name: values.name,
          specialty: specialtyArray,
          experience: experienceArray,
          education: educationData,
          description: values.description,
          profile_image: values.profile_image || undefined,
        },
        token,
      )

      if (result.success) {
        toast.success('新增醫生成功')
        form.reset()
        setCurrentProfileImageUuid(null)
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error(result.error?.message || '新增醫生失敗')
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? '編輯醫生資料' : '新增醫生'}</DialogTitle>
          <DialogDescription>{isEditMode ? '更新醫生資訊' : '新增醫生至診所'}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>醫生姓名</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入醫生姓名" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="specialty"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>專科領域</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Card>
                        <CardContent className="p-3">
                          <div className="flex flex-wrap gap-2">
                            {field.value.map((specialty: string, index: number) => (
                              <Badge key={index} className="px-3 py-1">
                                {specialty}
                                <button
                                  type="button"
                                  className="ml-2 text-xs"
                                  onClick={() => {
                                    const newSpecialties = [...field.value]
                                    newSpecialties.splice(index, 1)
                                    field.onChange(newSpecialties)
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-full"
                              onClick={() => setSpecialtyDialogOpen(true)}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" />
                              新增專科
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Dialog open={specialtyDialogOpen} onOpenChange={setSpecialtyDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>新增專科領域</DialogTitle>
                  <DialogDescription>請輸入醫生的專科領域</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Input
                      id="specialty"
                      className="col-span-4"
                      placeholder="輸入專科領域"
                      value={newSpecialty}
                      onChange={(e) => setNewSpecialty(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!newSpecialty) return
                      const currentSpecialties = form.getValues('specialty')
                      form.setValue('specialty', [...currentSpecialties, newSpecialty])
                      setNewSpecialty('')
                      setSpecialtyDialogOpen(false)
                    }}
                  >
                    新增
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <FormField
              control={form.control}
              name="experience"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>經歷</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Card>
                        <CardContent className="p-3">
                          <div className="flex flex-wrap gap-2">
                            {field.value.map((experience: string, index: number) => (
                              <Badge key={index} className="px-3 py-1">
                                {experience}
                                <button
                                  type="button"
                                  className="ml-2 text-xs"
                                  onClick={() => {
                                    const newExperiences = [...field.value]
                                    newExperiences.splice(index, 1)
                                    field.onChange(newExperiences)
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-full"
                              onClick={() => setExperienceDialogOpen(true)}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" />
                              新增經歷
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Dialog open={experienceDialogOpen} onOpenChange={setExperienceDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>新增經歷</DialogTitle>
                  <DialogDescription>請輸入醫生的經歷</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Input
                      id="experience"
                      className="col-span-4"
                      placeholder="輸入經歷"
                      value={newExperience}
                      onChange={(e) => setNewExperience(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!newExperience) return
                      const currentExperiences = form.getValues('experience')
                      form.setValue('experience', [...currentExperiences, newExperience])
                      setNewExperience('')
                      setExperienceDialogOpen(false)
                    }}
                  >
                    新增
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <FormField
              control={form.control}
              name="education"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>教育背景</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Card>
                        <CardContent className="p-3">
                          <div className="flex flex-wrap gap-2">
                            {field.value.map(
                              (edu: { school?: string; year?: string }, index: number) =>
                                edu.school && (
                                  <Badge key={index} className="px-3 py-1">
                                    {edu.school} {edu.year ? `(${edu.year})` : ''}
                                    <button
                                      type="button"
                                      className="ml-2 text-xs"
                                      onClick={() => {
                                        const newEducation = [...field.value]
                                        newEducation.splice(index, 1)
                                        field.onChange(newEducation)
                                      }}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </Badge>
                                ),
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 rounded-full"
                              onClick={() => setEducationDialogOpen(true)}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" />
                              新增教育背景
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Dialog open={educationDialogOpen} onOpenChange={setEducationDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>新增教育背景</DialogTitle>
                  <DialogDescription>請輸入醫生的教育背景</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="school" className="text-right">
                      學校
                    </Label>
                    <Input
                      id="school"
                      className="col-span-3"
                      placeholder="輸入學校名稱"
                      value={newSchool}
                      onChange={(e) => setNewSchool(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="year" className="text-right">
                      年份
                    </Label>
                    <Input
                      id="year"
                      className="col-span-3"
                      placeholder="輸入年份（選填）"
                      value={newYear}
                      onChange={(e) => setNewYear(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!newSchool) return
                      const currentEducation = form.getValues('education')
                      form.setValue('education', [
                        ...currentEducation,
                        {
                          school: newSchool,
                          year: newYear || '',
                        },
                      ])
                      setNewSchool('')
                      setNewYear('')
                      setEducationDialogOpen(false)
                    }}
                  >
                    新增
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>醫生簡介</FormLabel>
                  <FormControl>
                    <Textarea placeholder="請輸入醫生簡介" className="min-h-[80px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="profile_image"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>醫生照片</FormLabel>
                  <FormControl>
                    <div className="mt-2">
                      {currentProfileImageUuid ? (
                        <div className="relative group mb-4 inline-block">
                          <img
                            src={`${import.meta.env.VITE_API_URL}/api/files/download/${currentProfileImageUuid}`}
                            alt="醫生照片"
                            className="h-32 w-32 object-cover rounded-md"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            type="button"
                            className="absolute right-1 top-1 h-6 w-6 hidden group-hover:flex items-center justify-center"
                            onClick={() => {
                              field.onChange('')
                              setCurrentProfileImageUuid(null)
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Uploader
                          folder="doctors"
                          allowedFileTypes={['.jpeg', '.jpg', '.png', '.webp']}
                          onUploadSuccess={(result) => {
                            field.onChange(result.uuid)
                            setCurrentProfileImageUuid(result.uuid)
                          }}
                          onDelete={() => {
                            // This onDelete for Uploader might be for its internal state if it supports it, or could be removed if not used
                            field.onChange('')
                            setCurrentProfileImageUuid(null) // Ensure consistency
                          }}
                          className="w-full"
                        />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit">{isEditMode ? '更新' : '新增'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
